import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import api from 'utils/api';
import { useProviderStore } from 'Store/ProviderStore';

const ServiceManagement = () => {
  const provider = useProviderStore((state) => state.provider);

  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);

  const [searchTerm, setSearchTerm] = useState('');

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [selectedService, setSelectedService] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    provider: provider?._id,
    name: '',
    category: '',
    duration: '',
    price: '',
    description: '',
  });

  // ---------------------- Fetch Services --------------------------------------
  const getServices = async () => {
    // اگر آی‌دی پرووایدر وجود ندارد، درخواست نزن
    if (!provider?._id) {
      console.warn('Provider ID is missing, skipping fetch.');
      return;
    }

    try {
      console.log('Fetching services for provider:', provider._id);
      const response = await api.get(`/providers/${provider._id}/services`);
      console.log('Services fetched:', response.data);
      setServices(response.data);
    } catch (err) {
      console.error('Error fetching services:', err);
    }
  };

  // ---------------------- Fetch Categories ------------------------------------
  const getCategories = async () => {
    try {
      const response = await api.get(`/categories`);
      setCategories(response.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  // ✅ اصلاح مهم: اضافه کردن وابستگی به provider._id
  useEffect(() => {
    if (provider?._id) {
      getServices();
    }
    getCategories();
  }, [provider?._id]); // هر وقت ID پرووایدر تغییر کرد یا لود شد، دوباره درخواست بزن

  // ---------------------- Filter Search ---------------------------------------
  const filteredServices = services.filter((service) =>
    service.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ---------------------- ADD SERVICE -----------------------------------------
  const handleAddService = () => {
    setFormData({
      provider: provider._id,
      name: '',
      category: '',
      duration: '',
      price: '',
      description: '',
    });
    setShowAddModal(true);
  };

  // ---------------------- EDIT SERVICE ----------------------------------------
  const handleEditService = (service) => {
    setSelectedService(service);

    const cat = categories.find((c) => c.name === service.category);

    setFormData({
      provider: provider._id,
      name: service.name,
      category: cat?._id || '', // ذخیره id ولی نمایش name با populate
      duration: service.duration?.toString() || '',
      price: service.price?.toString() || '',
      description: service.description || '',
    });

    setShowEditModal(true);
  };

  // ---------------------- DELETE CONFIRM --------------------------------------
  const openDeleteModal = (service) => {
    setSelectedService(service);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/services/delete/${selectedService._id}`);
      setShowDeleteModal(false);
      setSelectedService(null);
      await getServices();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  // ---------------------- SUBMIT FORM (Add + Edit) ----------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ...formData,
      duration: parseInt(formData.duration),
      price: parseFloat(formData.price),
    };

    try {
      if (showEditModal && selectedService) {
        await api.put(
          `/services/providers/${provider._id}/${selectedService._id}`,
          payload
        );
      } else {
        await api.post(`/providers/${provider._id}/services`, payload);
      }

      setShowAddModal(false);
      setShowEditModal(false);
      await getServices();
    } catch (error) {
      console.error('SAVE ERROR:', error);
    }
  };

  // STYLE HELPERS
  const formatPrice = (p) =>
    new Intl.NumberFormat('fa-IR').format(p) + ' تومان';
  const formatDuration = (d) => `${d} دقیقه`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
        <div>
          <h2 className="text-xl font-semibold">مدیریت خدمات</h2>
          <p className="text-sm text-muted-foreground mt-1">
            تنظیم خدمات، قیمت‌گذاری و مدت زمان ارائه
          </p>
        </div>
        <Button variant="default" onClick={handleAddService} iconName="Plus">
          افزودن خدمت جدید
        </Button>
      </div>

      {/* Search */}
      <Input
        placeholder="جستجو..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      {/* Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden hidden lg:block">
        <table className="w-full">
          <thead className="bg-muted/50 border-b">
            <tr>
              <th className="px-6 py-4 text-right">نام خدمت</th>
              <th className="px-6 py-4 text-right">دسته‌بندی</th>
              <th className="px-6 py-4 text-right">مدت زمان</th>
              <th className="px-6 py-4 text-right">قیمت</th>
              <th className="px-6 py-4 text-center">عملیات</th>
            </tr>
          </thead>
          <tbody>
            {filteredServices.map((service) => (
              <tr key={service._id} className="border-b hover:bg-muted/30">
                <td className="px-6 py-4">{service.name}</td>
                <td className="px-6 py-4">{service.category}</td>
                <td className="px-6 py-4">
                  {formatDuration(service.duration)}
                </td>
                <td className="px-6 py-4">{formatPrice(service.price)}</td>
                <td className="px-6 py-4 text-center flex justify-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    iconName="Edit"
                    onClick={() => handleEditService(service)}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    iconName="Trash2"
                    className="text-error"
                    onClick={() => openDeleteModal(service)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ----------------- ADD / EDIT MODAL ----------------- */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-xl rounded-lg shadow-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {showAddModal ? 'افزودن خدمت جدید' : 'ویرایش خدمت'}
              </h3>
              <Button
                variant="ghost"
                size="icon"
                iconName="X"
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                }}
              />
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label="نام خدمت"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
              />

              <Select
                label="دسته‌بندی"
                options={categories.map((c) => ({
                  label: c.name,
                  value: c._id,
                }))}
                value={formData.category}
                onChange={(value) =>
                  setFormData({ ...formData, category: value })
                }
                required
              />

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="مدت زمان (دقیقه)"
                  type="number"
                  value={formData.duration}
                  onChange={(e) =>
                    setFormData({ ...formData, duration: e.target.value })
                  }
                  required
                />
                <Input
                  label="قیمت"
                  type="number"
                  value={formData.price}
                  onChange={(e) =>
                    setFormData({ ...formData, price: e.target.value })
                  }
                  required
                />
              </div>

              <textarea
                className="w-full border p-3 rounded-md"
                placeholder="توضیحات خدمت..."
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                rows={4}
              />

              <div className="flex gap-3 pt-3">
                <Button type="submit" className="flex-1">
                  {showAddModal ? 'افزودن' : 'ذخیره تغییرات'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                  }}>
                  انصراف
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ----------------- DELETE CONFIRM MODAL ----------------- */}
      {showDeleteModal && selectedService && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-md p-6 rounded-lg shadow-lg">
            <h3 className="text-lg font-semibold mb-2">حذف خدمت</h3>
            <p className="text-sm mb-6">
              آیا از حذف خدمت <strong>{selectedService.name}</strong> اطمینان
              دارید؟
            </p>

            <div className="flex gap-3">
              <Button
                variant="destructive"
                className="flex-1"
                onClick={confirmDelete}>
                حذف
              </Button>
              <Button
                className="flex-1"
                variant="outline"
                onClick={() => setShowDeleteModal(false)}>
                انصراف
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceManagement;
